import React, { useState } from 'react';
import './AdvertisingPage.css';

function AdvertisingPage({ currentUser }) {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    discord: '',
    package: '',
    duration: '1',
    message: ''
  });

  const packages = {
    server: [
      {
        id: 'basic-ad',
        name: 'Basic Ad',
        price: 5,
        period: 'month',
        description: 'One message in general or info channel per week. Sponsor role in server.',
        features: ['One message per week', 'Sponsor role', 'Server visibility']
      },
      {
        id: 'standard-ad',
        name: 'Standard Ad',
        price: 10,
        period: 'month',
        description: 'Two messages per week. Sponsor role with special color. Mention in one event.',
        features: ['Two messages per week', 'Special color role', 'Event mention']
      },
      {
        id: 'premium-ad',
        name: 'Premium Ad',
        price: 20,
        period: 'month',
        description: 'Daily message for one week. Sponsor role with badge. Mention in all events that month.',
        features: ['Daily messages (1 week)', 'Badge role', 'All events mention']
      }
    ],
    website: [
      {
        id: 'banner-ad',
        name: 'Banner Ad',
        price: 5,
        period: 'month',
        description: 'Small banner on homepage or sidebar with clickable link.',
        features: ['Homepage or sidebar banner', 'Clickable link', 'Full month visibility']
      },
      {
        id: 'sponsored-post',
        name: 'Sponsored Post',
        price: 10,
        period: 'post',
        description: 'One post featuring sponsor. Shared in server and social media.',
        features: ['Featured post', 'Server announcement', 'Social media share']
      }
    ],
    partnership: [
      {
        id: 'basic-partner',
        name: 'Basic Partner',
        price: 7.5,
        period: 'month',
        description: 'Mutual promotion in server. Custom partner role.',
        features: ['Mutual promotion', 'Partner role', 'Server visibility']
      },
      {
        id: 'standard-partner',
        name: 'Standard Partner',
        price: 15,
        period: 'month',
        description: 'Featured in server and pinned messages. Partner badge/role.',
        features: ['Featured placement', 'Pinned messages', 'Partner badge']
      },
      {
        id: 'premium-partner',
        name: 'Premium Partner',
        price: 25,
        period: 'month',
        description: 'Mention in all events for the month. Featured in server + website banner.',
        features: ['All events mention', 'Website banner', 'Premium placement']
      }
    ]
  };

  const handlePackageSelect = (pkg, category) => {
    setSelectedPackage({ ...pkg, category });
    setFormData({ ...formData, package: pkg.id });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create purchase request
    const purchase = {
      id: Date.now(),
      user: currentUser ? currentUser.discordUsername : formData.name,
      email: formData.email,
      discord: formData.discord || currentUser?.discordUsername,
      package: formData.package,
      duration: formData.duration,
      message: formData.message,
      status: 'pending',
      createdAt: new Date().toISOString()
    };

    // Save to localStorage
    const purchases = JSON.parse(localStorage.getItem('nitroPurchases') || '[]');
    purchases.push(purchase);
    localStorage.setItem('nitroPurchases', JSON.stringify(purchases));

    // Send notification (could integrate with webhook)
    alert('Your advertising request has been submitted! We will contact you soon via email or Discord.');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      discord: '',
      package: '',
      duration: '1',
      message: ''
    });
    setSelectedPackage(null);
  };

  return (
    <div className="advertising-page">
      <div className="advertising-header">
        <h1 className="section-title">📢 Advertising & Sponsorship</h1>
        <p className="advertising-intro">
          Promote your Discord bot, server, website, or products to thousands of active users!
        </p>
      </div>

      <div className="packages-section">
        <div className="package-category">
          <h2>1. Server Sponsorship / Advertising</h2>
          <div className="packages-grid">
            {packages.server.map(pkg => (
              <div
                key={pkg.id}
                className={`package-card ${selectedPackage?.id === pkg.id ? 'selected' : ''}`}
                onClick={() => handlePackageSelect(pkg, 'server')}
              >
                <div className="package-header">
                  <h3>{pkg.name}</h3>
                  <div className="package-price">
                    €{pkg.price}
                    <span>/{pkg.period}</span>
                  </div>
                </div>
                <p className="package-description">{pkg.description}</p>
                <ul className="package-features">
                  {pkg.features.map((feature, index) => (
                    <li key={index}>✓ {feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="package-category">
          <h2>2. Website Advertising</h2>
          <div className="packages-grid">
            {packages.website.map(pkg => (
              <div
                key={pkg.id}
                className={`package-card ${selectedPackage?.id === pkg.id ? 'selected' : ''}`}
                onClick={() => handlePackageSelect(pkg, 'website')}
              >
                <div className="package-header">
                  <h3>{pkg.name}</h3>
                  <div className="package-price">
                    €{pkg.price}
                    <span>/{pkg.period}</span>
                  </div>
                </div>
                <p className="package-description">{pkg.description}</p>
                <ul className="package-features">
                  {pkg.features.map((feature, index) => (
                    <li key={index}>✓ {feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="package-category">
          <h2>3. Partnerships</h2>
          <div className="packages-grid">
            {packages.partnership.map(pkg => (
              <div
                key={pkg.id}
                className={`package-card ${selectedPackage?.id === pkg.id ? 'selected' : ''}`}
                onClick={() => handlePackageSelect(pkg, 'partnership')}
              >
                <div className="package-header">
                  <h3>{pkg.name}</h3>
                  <div className="package-price">
                    €{pkg.price}
                    <span>/{pkg.period}</span>
                  </div>
                </div>
                <p className="package-description">{pkg.description}</p>
                <ul className="package-features">
                  {pkg.features.map((feature, index) => (
                    <li key={index}>✓ {feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="purchase-form-section">
        <h2>Purchase Advertising Package</h2>
        {selectedPackage ? (
          <div className="selected-package-info">
            <h3>Selected: {selectedPackage.name}</h3>
            <p>€{selectedPackage.price} / {selectedPackage.period}</p>
          </div>
        ) : (
          <p className="select-package-hint">Please select a package above to proceed</p>
        )}

        <form className="purchase-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required={!currentUser}
                placeholder={currentUser ? currentUser.discordUsername : 'Your name'}
                disabled={!!currentUser}
              />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="your@email.com"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Discord Username</label>
            <input
              type="text"
              value={formData.discord}
              onChange={(e) => setFormData({ ...formData, discord: e.target.value })}
              placeholder={currentUser ? currentUser.discordUsername : 'username#1234'}
              disabled={!!currentUser}
            />
          </div>

          {selectedPackage && (
            <>
              <div className="form-group">
                <label>Duration</label>
                <select
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                >
                  <option value="1">1 {selectedPackage.period === 'month' ? 'month' : 'time'}</option>
                  <option value="3">3 {selectedPackage.period === 'month' ? 'months' : 'times'}</option>
                  <option value="6">6 {selectedPackage.period === 'month' ? 'months' : 'times'}</option>
                  <option value="12">12 {selectedPackage.period === 'month' ? 'months' : 'times'}</option>
                </select>
              </div>

              <div className="price-calculation">
                <div className="price-line">
                  <span>Package:</span>
                  <span>€{selectedPackage.price} / {selectedPackage.period}</span>
                </div>
                <div className="price-line">
                  <span>Duration:</span>
                  <span>{formData.duration} {selectedPackage.period === 'month' ? 'month(s)' : 'time(s)'}</span>
                </div>
                <div className="price-total">
                  <span>Total:</span>
                  <span>€{(selectedPackage.price * parseInt(formData.duration)).toFixed(2)}</span>
                </div>
              </div>
            </>
          )}

          <div className="form-group">
            <label>Message (Optional)</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Tell us about your bot/server/website or any special requirements..."
              rows="4"
            />
          </div>

          <button 
            type="submit" 
            className="submit-btn"
            disabled={!selectedPackage}
          >
            Submit Purchase Request
          </button>

          <p className="form-note">
            After submitting, we will contact you via email or Discord to complete payment and setup.
          </p>
        </form>
      </div>

      <div className="contact-info">
        <h3>Need Help?</h3>
        <p>Contact us for custom packages or questions:</p>
        <ul>
          <li>Discord: <strong>0b.livion</strong> (DM)</li>
          <li>Email: <strong>nitrodrop@minister.com</strong></li>
        </ul>
      </div>
    </div>
  );
}

export default AdvertisingPage;

